/**
 * 蒸馏建议面板（B2 蒸馏落地闭环前端）。
 *
 * 数据源（异步任务 + SSE 事件流）：
 * - POST /sessions/{id}/persona-analysis/jobs 创建后台分析任务，立即返回 {job_id}；
 * - streamJobEvents 订阅 /api/v1/jobs/{job_id}/events（先 events/history 游标重放
 *   再接 SSE），done 事件 data.result 带完整分析结果；failed 带中文错误；
 * - 进行中 job_id 存 sessionStorage，刷新/重开面板自动恢复订阅（参照 DeployProgress）；
 * - GET /agents/{id}/persona 取当前人设，用于 before/after diff。
 *
 * 落库：POST /sessions/{id}/distill/apply（用户勾选后的条目子集）。
 *
 * - 人设增量用 ConfigDiffViewer 展示 before/after diff；
 * - 边界规则 / 知识条目 / 工具建议以勾选列表展示；
 * - 加载失败诚实降级（提示 + 重试），不阻塞聊天主流程。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { X, Sparkles, Loader2, RefreshCw, AlertTriangle } from 'lucide-react';
import apiClient from '../api/client';
import { streamJobEvents } from '../api/sse';
import { useToast } from './ui/toastContext';
import ConfigDiffViewer from './ConfigDiffViewer';

interface KnowledgeSuggestion {
  content: string;
  category?: string;
  confidence?: number;
}

interface ToolSuggestion {
  suggested_tool: string;
  reason?: string;
}

interface PersonaAnalysisResponse {
  session_id: string;
  persona_delta: Record<string, unknown>;
  knowledge_items: KnowledgeSuggestion[];
  boundary_rules: string[];
  tool_suggestions: ToolSuggestion[];
  style_markers: Record<string, unknown>;
  feedback_stats: { correct: number; incorrect: number; partial: number; uncertain: number };
  degraded: boolean;
  llm_used: boolean;
}

interface DistillSuggestionsPanelProps {
  sessionId: string;
  agentId: string;
  open: boolean;
  onClose: () => void;
}

/** 面板阶段：idle=待开始 / running=任务进行中 / done=已有结果 / error=失败可重试 */
type PanelPhase = 'idle' | 'running' | 'done' | 'error';

const bodyStyle: CSSProperties = { flex: 1, overflowY: 'auto', padding: 20 };
const sectionTitleStyle: CSSProperties = {
  fontSize: 13,
  color: 'var(--text-muted)',
  margin: '0 0 8px',
};
const itemStyle: CSSProperties = {
  display: 'flex',
  gap: 8,
  alignItems: 'flex-start',
  padding: '10px 12px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-md)',
  marginBottom: 8,
  background: 'var(--bg-primary)',
};

export default function DistillSuggestionsPanel({
  sessionId,
  agentId,
  open,
  onClose,
}: DistillSuggestionsPanelProps) {
  const toast = useToast();
  const [phase, setPhase] = useState<PanelPhase>('idle');
  const [error, setError] = useState('');
  const [analysis, setAnalysis] = useState<PersonaAnalysisResponse | null>(null);
  const [currentPersona, setCurrentPersona] = useState<Record<string, unknown>>({});
  const abortRef = useRef<AbortController | null>(null);

  const [personaChecked, setPersonaChecked] = useState(false);
  const [boundaryChecked, setBoundaryChecked] = useState<Set<number>>(new Set());
  const [knowledgeChecked, setKnowledgeChecked] = useState<Set<number>>(new Set());
  const [toolChecked, setToolChecked] = useState<Set<number>>(new Set());
  const [applying, setApplying] = useState(false);

  /** 进行中任务的恢复键（sessionStorage，刷新页面后续传不断档） */
  const jobStorageKey = `distill-analysis-job:${sessionId}`;

  const resetChecks = useCallback(() => {
    setPersonaChecked(false);
    setBoundaryChecked(new Set());
    setKnowledgeChecked(new Set());
    setToolChecked(new Set());
  }, []);

  const hasPersonaDelta = useMemo(
    () => !!analysis?.persona_delta && Object.keys(analysis.persona_delta).length > 0,
    [analysis],
  );

  const mergedPersona = useMemo(
    () => ({ ...(currentPersona || {}), ...(analysis?.persona_delta || {}) }),
    [analysis, currentPersona],
  );

  const hasAnySuggestion = useMemo(
    () =>
      !!analysis &&
      (hasPersonaDelta ||
        (analysis.boundary_rules || []).length > 0 ||
        (analysis.knowledge_items || []).length > 0 ||
        (analysis.tool_suggestions || []).length > 0),
    [analysis, hasPersonaDelta],
  );

  const hasSelection =
    personaChecked || boundaryChecked.size > 0 || knowledgeChecked.size > 0 || toolChecked.size > 0;

  const running = phase === 'running';

  /** 订阅任务事件流：历史游标重放 + SSE 实时；done 终态取结果渲染。 */
  const followJob = useCallback(
    async (jobId: string) => {
      setPhase('running');
      setError('');
      const ac = new AbortController();
      abortRef.current?.abort();
      abortRef.current = ac;
      let finished = false;
      const fail = (message: string) => {
        sessionStorage.removeItem(jobStorageKey);
        setError(message);
        setPhase('error');
      };
      try {
        await streamJobEvents(
          jobId,
          (event) => {
            if (event.type !== 'done') return;
            finished = true;
            const data = event.data as { status?: string; result?: PersonaAnalysisResponse; error?: string };
            sessionStorage.removeItem(jobStorageKey);
            if (data.status === 'succeeded' && data.result) {
              setAnalysis(data.result);
              resetChecks();
              setPhase('done');
            } else {
              setError(data.error || '分析失败，请稍后重试');
              setPhase('error');
            }
          },
          ac.signal,
        );
        if (!finished && !ac.signal.aborted) {
          fail('分析任务事件流中断，请重试');
        }
      } catch {
        if (!ac.signal.aborted) {
          fail('分析任务事件流中断，请重试');
        }
      }
    },
    [jobStorageKey, resetChecks],
  );

  /** 创建后台分析任务并订阅事件流。 */
  const startAnalysis = useCallback(async () => {
    setPhase('running');
    setError('');
    try {
      const res = await apiClient.post(`/sessions/${sessionId}/persona-analysis/jobs`, null, {
        params: agentId ? { agent_id: agentId } : {},
      });
      const { job_id } = res.data as { job_id: string };
      sessionStorage.setItem(jobStorageKey, job_id);
      await followJob(job_id);
    } catch {
      setError('创建分析任务失败，请稍后重试');
      setPhase('error');
    }
  }, [sessionId, agentId, jobStorageKey, followJob]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void (async () => {
      // 当前人设用于 diff；加载失败不阻塞分析流程（diff 退化为空对象）
      try {
        const personaRes = await apiClient.get(`/agents/${agentId}/persona`);
        if (!cancelled) setCurrentPersona(personaRes.data || {});
      } catch {
        /* 忽略：人设 diff 兜底为空 */
      }
      // 恢复进行中的任务（刷新/重开面板续传，done 事件后自动渲染结果）
      const stored = sessionStorage.getItem(jobStorageKey);
      if (stored && !cancelled) {
        await followJob(stored);
      }
    })();
    return () => {
      cancelled = true;
      abortRef.current?.abort();
    };
  }, [open, agentId, jobStorageKey, followJob]);

  const toggleSet = (setter: React.Dispatch<React.SetStateAction<Set<number>>>, idx: number) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  const applySelected = async () => {
    if (!analysis || !hasSelection || applying) return;
    setApplying(true);
    const payload: Record<string, unknown> = {};
    if (personaChecked && hasPersonaDelta) {
      payload.persona_delta = analysis.persona_delta;
    }
    const boundary = [...boundaryChecked].map((i) => analysis.boundary_rules[i]);
    if (boundary.length) {
      payload.boundary_rules = boundary;
    }
    const knowledge = [...knowledgeChecked].map((i) => {
      const item = analysis.knowledge_items[i];
      return { content: item.content, category: item.category ?? 'concept', confidence: item.confidence ?? 1.0 };
    });
    if (knowledge.length) {
      payload.knowledge_items = knowledge;
    }
    const tools = [...toolChecked].map((i) => ({
      suggested_tool: analysis.tool_suggestions[i].suggested_tool,
      reason: analysis.tool_suggestions[i].reason ?? '',
    }));
    if (tools.length) {
      payload.tool_suggestions = tools;
    }
    if (analysis.feedback_stats) {
      payload.feedback_stats = analysis.feedback_stats;
    }
    try {
      const res = await apiClient.post(`/sessions/${sessionId}/distill/apply`, payload);
      const data = res.data as { applied_count: number; skipped_count: number };
      toast.success(
        `已应用 ${data.applied_count} 项蒸馏建议` + (data.skipped_count ? `，跳过 ${data.skipped_count} 项` : ''),
      );
      onClose();
    } catch (e) {
      const err = e as { response?: { data?: { detail?: unknown } } };
      const detail = err.response?.data?.detail;
      toast.error('应用蒸馏建议失败' + (typeof detail === 'string' ? `：${detail}` : ''));
    } finally {
      setApplying(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <div
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 150 }}
        onClick={onClose}
      />
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: '100%',
          maxWidth: 560,
          height: '100dvh',
          background: 'var(--bg-secondary)',
          borderLeft: '1px solid var(--border-default)',
          zIndex: 160,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 'var(--shadow-lg)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid var(--border-default)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div className="flex items-center gap-2">
            <Sparkles size={18} color="var(--color-primary)" />
            <h3 style={{ margin: 0, fontSize: 16 }}>蒸馏建议</h3>
            {analysis?.degraded && (
              <span className="badge warning" title="未配置 LLM 或调用失败，分析结果为启发式推导">
                启发式降级
              </span>
            )}
          </div>
          <button className="secondary" style={{ padding: '6px 8px' }} onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div style={bodyStyle}>
          {phase === 'idle' && (
            <div className="text-center" style={{ padding: '40px 0' }}>
              <Sparkles size={22} color="var(--color-primary)" style={{ marginBottom: 8 }} />
              <p className="text-muted" style={{ margin: '0 0 16px' }}>
                分析本对话，提炼人设增量、边界规则、知识条目与工具建议。
                <br />
                分析在后台异步执行，进行中可刷新页面自动恢复。
              </p>
              <button className="primary" onClick={() => void startAnalysis()}>
                <Sparkles size={14} /> 开始分析
              </button>
            </div>
          )}

          {running && (
            <div className="flex items-center gap-2" style={{ padding: '40px 0', justifyContent: 'center' }}>
              <Loader2 size={18} className="animate-spin" />
              <span className="text-muted">正在后台分析对话，请稍候（可刷新页面，完成后自动展示）…</span>
            </div>
          )}

          {!running && phase === 'error' && (
            <div className="text-center" style={{ padding: '40px 0' }}>
              <AlertTriangle size={20} color="var(--color-warning)" style={{ marginBottom: 8 }} />
              <p style={{ margin: '0 0 12px' }}>{error}</p>
              <button className="secondary" onClick={() => void startAnalysis()}>
                <RefreshCw size={14} /> 重试
              </button>
            </div>
          )}

          {phase === 'done' && analysis && !hasAnySuggestion && (
            <div className="text-center" style={{ padding: '40px 0' }}>
              <p className="text-muted">暂无可应用的蒸馏建议，继续对话积累示教内容后再试。</p>
            </div>
          )}

          {phase === 'done' && analysis && hasAnySuggestion && (
            <>
              {analysis.degraded && (
                <div className="badge warning mb-4" style={{ display: 'block', padding: 10 }}>
                  分析基于启发式降级（未配置 LLM 或调用失败），建议仅供参考。
                </div>
              )}
              {analysis.feedback_stats &&
                (analysis.feedback_stats.incorrect > 0 || analysis.feedback_stats.partial > 0) && (
                  <div className="badge info mb-4" style={{ display: 'block', padding: 10 }}>
                    示教反馈：正确 {analysis.feedback_stats.correct} · 部分 {analysis.feedback_stats.partial} ·
                    错误 {analysis.feedback_stats.incorrect}（反馈统计已注入分析上下文）
                  </div>
                )}

              {/* 人设增量 */}
              {hasPersonaDelta && (
                <div style={{ marginBottom: 20 }}>
                  <h4 style={sectionTitleStyle}>人设增量</h4>
                  <div style={itemStyle}>
                    <input
                      type="checkbox"
                      checked={personaChecked}
                      onChange={(e) => setPersonaChecked(e.target.checked)}
                      style={{ marginTop: 3 }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ marginBottom: 6, fontSize: 13 }}>
                        合并人设增量（冲突字段以本次确认为准）
                      </div>
                      <ConfigDiffViewer left={currentPersona} right={mergedPersona} />
                    </div>
                  </div>
                </div>
              )}

              {/* 边界规则 */}
              {analysis.boundary_rules.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <h4 style={sectionTitleStyle}>边界规则</h4>
                  {analysis.boundary_rules.map((rule, i) => (
                    <div key={`b-${i}`} style={itemStyle}>
                      <input
                        type="checkbox"
                        checked={boundaryChecked.has(i)}
                        onChange={() => toggleSet(setBoundaryChecked, i)}
                        style={{ marginTop: 3 }}
                      />
                      <span style={{ fontSize: 13, lineHeight: 1.6 }}>{rule}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* 知识条目 */}
              {analysis.knowledge_items.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <h4 style={sectionTitleStyle}>知识条目</h4>
                  {analysis.knowledge_items.map((item, i) => (
                    <div key={`k-${i}`} style={itemStyle}>
                      <input
                        type="checkbox"
                        checked={knowledgeChecked.has(i)}
                        onChange={() => toggleSet(setKnowledgeChecked, i)}
                        style={{ marginTop: 3 }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, lineHeight: 1.6 }}>{item.content}</div>
                        <div className="text-muted" style={{ fontSize: 12, marginTop: 2 }}>
                          分类：{item.category || 'concept'} · 置信度 {(item.confidence ?? 1.0).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* 工具建议 */}
              {analysis.tool_suggestions.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <h4 style={sectionTitleStyle}>工具建议（将生成为禁用态技能草稿）</h4>
                  {analysis.tool_suggestions.map((item, i) => (
                    <div key={`t-${i}`} style={itemStyle}>
                      <input
                        type="checkbox"
                        checked={toolChecked.has(i)}
                        onChange={() => toggleSet(setToolChecked, i)}
                        style={{ marginTop: 3 }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, lineHeight: 1.6 }}>{item.suggested_tool}</div>
                        {item.reason && (
                          <div className="text-muted" style={{ fontSize: 12, marginTop: 2 }}>
                            {item.reason}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid var(--border-default)',
            display: 'flex',
            gap: 12,
          }}
        >
          <button className="secondary" style={{ flex: 1 }} onClick={onClose}>
            取消
          </button>
          <button
            className="primary"
            style={{ flex: 2 }}
            disabled={phase !== 'done' || !hasSelection || applying}
            onClick={() => void applySelected()}
          >
            {applying ? (
              <>
                <Loader2 size={14} className="animate-spin" /> 应用中…
              </>
            ) : (
              <>
                <Sparkles size={14} /> 确认应用
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
}
